import api from './browserApi';
import { getAuthDeviceContext } from './authDevice';

// =================================================================
// Helpers (JS puro)
// =================================================================
function pick(obj, keys) {
  const out = {};
  keys.forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined) {
      out[k] = obj[k];
    }
  });
  return out;
}

// =================================================================
/** AUTENTICAÇÃO */
// =================================================================
export const login = (username, password) =>
  api.post('/auth/v2/login', {
    username,
    password,
    ...getAuthDeviceContext(),
  });

export const register = (username, password) =>
  api.post('/register', { username, password });

export const requestPasswordRecovery = (email) =>
  api.post('/auth/v2/forgot-password', { email });

export const resetPassword = (payload) =>
  api.post('/auth/v2/reset-password', payload);

export const signupFoodstation = (payload) =>
  api.post('/auth/v2/signup', {
    ...payload,
    ...getAuthDeviceContext(),
  });

export const getPublicPromotion = (token) =>
  api.get(`/promotions/public/${encodeURIComponent(token)}`);

export const getAssignedPromotion = () =>
  api.get('/promotions/assigned');

export const changePassword = (payload) =>
  api.put('/auth/change-password', payload);

export const getCoreConfigOverview = () =>
  api.get('/config/core-config/overview').then((r) => r.data);

export const importCoreConfigSource = (source, options = {}) =>
  api.post('/config/core-config/import', {
    source,
    preview_only: options.previewOnly === true,
  }).then((r) => r.data);

export const getCoreConfigEditor = () =>
  api.get('/config/core-config/editor').then((r) => r.data);

export const saveCoreConfigEditor = (payload) =>
  api.put('/config/core-config/editor', payload).then((r) => r.data);

export const getCoreConfigModuleRules = () =>
  api.get('/config/core-config/module-rules').then((r) => r.data);

export const updateCoreConfigModuleRule = (modulo, payload) =>
  api.put(`/config/core-config/module-rules/${modulo}`, payload).then((r) => r.data);

export const geocodeConfigAddress = (address) =>
  api.get('/config/geocode', {
    params: { address },
  }).then((r) => r.data);

export const getIfoodImportStatus = () =>
  api.get('/ifood/status').then((r) => r.data);

export const getIfoodReviewsAvailability = () =>
  api.get('/ifood/reviews/availability').then((r) => r.data);

export const startIfoodAuthorization = () =>
  api.post('/ifood/authorization/start', {}).then((r) => r.data);

export const exchangeIfoodAuthorization = (authorizationCode) =>
  api.post('/ifood/authorization/exchange', {
    authorization_code: authorizationCode,
  }).then((r) => r.data);

export const listIfoodMerchants = ({ page = 1, size = 10 } = {}) =>
  api.get('/ifood/merchants', { params: { page, size } }).then((r) => r.data);

export const selectIfoodMerchant = (merchantId) =>
  api.put('/ifood/merchant', {
    merchant_id: merchantId,
  }).then((r) => r.data);

export const getIfoodMerchantDetails = (merchantId) =>
  api.get('/ifood/merchant/details', {
    params: {
      ...(merchantId ? { merchant_id: merchantId } : {}),
      include_opening_hours: false,
    },
  }).then((r) => r.data);

export const getIfoodMerchantOperations = (merchantId) =>
  api.get('/ifood/merchant/operations', {
    params: merchantId ? { merchant_id: merchantId } : undefined,
  }).then((r) => r.data);

export const createIfoodMerchantInterruption = (merchantId, payload) =>
  api.post('/ifood/merchant/interruptions', {
    merchant_id: merchantId,
    ...payload,
  }).then((r) => r.data);

export const deleteIfoodMerchantInterruption = (merchantId, interruptionId) =>
  api.delete(`/ifood/merchant/interruptions/${encodeURIComponent(interruptionId)}`, {
    params: merchantId ? { merchant_id: merchantId } : undefined,
  }).then((r) => r.data);

export const previewIfoodCatalog = (merchantId) =>
  api.get('/ifood/catalog/preview', {
    params: merchantId ? { merchant_id: merchantId } : undefined,
  }).then((r) => r.data);

export const importIfoodCatalog = (merchantId, options = {}) =>
  api.post('/ifood/catalog/import', {
    merchant_id: merchantId,
    ...(Array.isArray(options.itemIds) ? { item_ids: options.itemIds } : {}),
    deactivate_missing: options.deactivateMissing === true,
  }).then((r) => r.data);

export const previewIfoodMerchantHours = (merchantId) =>
  api.get('/ifood/merchant/hours/preview', {
    params: {
      ...(merchantId ? { merchant_id: merchantId } : {}),
      _ts: Date.now(),
    },
  }).then((r) => r.data);

export const importIfoodMerchantHours = (merchantId, scheduleDays) =>
  api.post('/ifood/merchant/hours/import', {
    merchant_id: merchantId,
    schedule_days: scheduleDays,
  }).then((r) => r.data);

export const updateIfoodMerchantHours = (merchantId, scheduleDays) =>
  api.put('/ifood/merchant/hours', {
    merchant_id: merchantId,
    schedule_days: scheduleDays,
  }).then((r) => r.data);

export const syncGlobalHoursToIfood = (merchantId) =>
  api.post('/ifood/merchant/hours/sync-global', {
    merchant_id: merchantId,
  }).then((r) => r.data);

export const getFeedbackIfoodSyncStatus = () =>
  api.get('/feedback/ifood/status').then((r) => r.data);

export const syncFeedbackIfoodReviews = ({ mode = 'incremental', force = false } = {}) =>
  api.post('/feedback/ifood/sync', { mode, force }).then((r) => r.data);

export const answerFeedbackIfoodReview = (feedbackId, text) =>
  api.post(`/feedback/feedbacks/${encodeURIComponent(feedbackId)}/ifood-answer`, { text })
    .then((r) => r.data);

export const getFeedbackReplySuggestions = (feedbackId) =>
  api.post(`/feedback/feedbacks/${encodeURIComponent(feedbackId)}/reply-suggestions`, {})
    .then((r) => r.data);

export const getGoogleBusinessStatus = () =>
  api.get('/google-business/status').then((r) => r.data);

export const startGoogleBusinessAuthorization = () =>
  api.post('/google-business/oauth/start', {}).then((r) => r.data);

export const listGoogleBusinessAccounts = () =>
  api.get('/google-business/accounts').then((r) => r.data);

export const listGoogleBusinessLocations = (accountName) =>
  api.get('/google-business/locations', {
    params: { account_name: accountName },
  }).then((r) => r.data);

export const selectGoogleBusinessLocation = (accountName, locationName) =>
  api.put('/google-business/location', {
    account_name: accountName,
    location_name: locationName,
  }).then((r) => r.data);

export const listGoogleBusinessReviews = (options = {}) =>
  api.get('/google-business/reviews', {
    params: {
      page_size: options.pageSize || 20,
      ...(options.pageToken ? { page_token: options.pageToken } : {}),
      ...(options.orderBy ? { order_by: options.orderBy } : {}),
    },
  }).then((r) => r.data);

export const replyGoogleBusinessReview = (reviewId, comment) =>
  api.put(`/google-business/reviews/${encodeURIComponent(reviewId)}/reply`, { comment })
    .then((r) => r.data);

export const getGoogleBusinessReplySuggestions = (reviewId) =>
  api.post(`/google-business/reviews/${encodeURIComponent(reviewId)}/reply-suggestions`, {})
    .then((r) => r.data);

export const disconnectGoogleBusiness = () =>
  api.delete('/google-business/connection').then((r) => r.data);

export const createCheckoutSession = (payload) =>
  api.post('/stripe/create-checkout-session', payload);

export const getProductCatalog = () =>
  api.get('/stripe/product-catalog');

export const getPublicProductCatalog = (config = {}) =>
  api.get('/stripe/public-product-catalog', config);

export const getBillingSummary = () =>
  api.get('/stripe/billing-summary');

export const cancelSubscription = () =>
  api.post('/stripe/cancel-subscription');

export const cancelProductSubscription = (productKey) =>
  api.post('/stripe/cancel-product-subscription', { productKey });

export const getProducts = () => api.get('/products');

export const saveProduct = (product) => {
  const productToSend = { ...product, principal: !!product.principal };
  return product.id
    ? api.put(`/products/${product.id}`, productToSend)
    : api.post('/products', productToSend);
};

export const deleteProduct = (productId) =>
  api.delete(`/products/${productId}`);

export const fetchMessages = (clienteId) =>
  api.get(`/atendabot/conversas/${clienteId}/mensagens`).then((r) => r.data);

export const sendMessage = (clienteId, mensagem) =>
  api
    .post(`/atendabot/conversas/${clienteId}/mensagens`, { mensagem })
    .then((r) => r.data);

export const sendBotImprovement = ({ botMessage, correction } = {}) =>
  api
    .post('/atendabot/forms/info-extra/improvement-answer', {
      mensagem_bot: botMessage,
      correcao: correction,
    })
    .then((r) => r.data);

export const sendClientImprovement = ({ clientMessage, answer } = {}) =>
  api
    .post('/atendabot/forms/respostas-cliente/improvement-answer', {
      mensagem_cliente: clientMessage,
      resposta_ideal: answer,
    })
    .then((r) => r.data);

export const sendAudioMessage = ({ phone, path, clienteId }) =>
  api
    .post('/api/audio/send', { phone, path, clienteId })
    .then((r) => r.data);

/**
 * @param {{ phone: any, path: any, clienteId?: string, caption?: string }} params
 */
export const sendImageMessage = ({ phone, path, clienteId, caption } = {}) =>
  api
    .post('/api/image/send', { phone, path, clienteId, caption })
    .then((r) => r.data);

export const markAsSeen = (clienteId) =>
  api.put(`/atendabot/clientes/${clienteId}/visualizar`, {}).then((r) => r.data);

export const switchToHuman = (clienteId) =>
  api.put(`/atendabot/atendimentos/${clienteId}/trocar`, {}).then((r) => r.data);

export const fetchBotStatus = async () => {
  const res = await api.get('/atendabot/config');
  return {
    atendabot_active: res.data.atendabot_active,
    isActive: res.data.atendabot_active,
    nome_no_atendimento: res.data.nome_no_atendimento === true,
  };
};

export const updateBotStatus = async (status) => {
  const isActive = typeof status === 'boolean' ? status : !!status?.isActive;
  await api.put('/atendabot/config', { atendabot_active: isActive });
  return {
    atendabot_active: isActive,
    isActive,
  };
};

// =================================================================
// LP Chat (landing page)
// =================================================================
export const sendLpChatMessage = ({ clientId, message, nome } = {}) =>
  api
    .post('/webhook/lp-chat', {
      clientId,
      message,
      nome,
      channel: 'lp_chat',
    })
    .then((r) => r.data);

export const fetchLpChatMessages = (clientId) =>
  api.get(`/webhook/lp-chat/${clientId}/mensagens`).then((r) => r.data);

// =================================================================
// Support Bot (produtos)
// =================================================================
export const getSupportBotBootstrap = (productKey) =>
  api.get(`/support-bot/bootstrap/${productKey}`).then((r) => r.data);

export const markSupportBotOpened = (productKey) =>
  api.post(`/support-bot/opened/${productKey}`, {}).then((r) => r.data);

export const sendSupportBotMessage = (productKey, payload = {}) =>
  api.post(`/support-bot/message/${productKey}`, payload).then((r) => r.data);

// =================================================================
// MÓDULO DE CARDÁPIO (sem mudanças de contrato)
// =================================================================
// --- Restaurante ---
export const getRestaurante = async () => {
  const tryGet = async (path, { keepArray = false } = {}) => {
    try {
      const { data } = await api.get(path);
      if (keepArray) return data ?? null;
      if (Array.isArray(data)) return data[0] || null;
      return data ?? null;
    } catch {
      return null;
    }
  };

  const candidates = ['/cardapio/restaurante'];

  for (const path of candidates) {
    const res = await tryGet(path);
    if (res && (res.nome || res.id)) return res;
  }

  for (const listPath of ['/restaurantes', '/cardapio/restaurantes']) {
    const list = await tryGet(listPath, { keepArray: true });
    if (Array.isArray(list) && list.length) {
      return list.find((r) => r.ativo) || list[0] || null;
    }
  }

  return null;
};

export const saveRestaurante = async (data) => {
  const allowed = ['id', 'restaurante_id', 'nome', 'foto_url', 'cardapio_logo_url', 'cardapio_bg_color', 'cardapio_bg_image_url', 'cardapio_layout', 'checkin_slug', 'ativo'];
  const body = pick(data || {}, allowed);
  const id = data?.id || data?.restaurante_id;
  const url = id ? `/cardapio/restaurante/${id}` : '/cardapio/restaurante';
  const res = await api.put(url, body, {
    params: !id && data?.restaurante_id ? { restauranteId: data.restaurante_id } : undefined,
  });
  return res.data?.data ?? res.data;
};

// --- Cardápios ---
export const listCardapios = (options = {}) =>
  api.get('/cardapio/cardapios', {
    params: options?.canal ? { canal: options.canal } : undefined,
  }).then((r) => r.data);

export const listCardapioEnderecos = () =>
  api.get('/cardapio/enderecos').then((r) => r.data);

export const saveCardapioEnderecoScope = (enderecoId, payload) =>
  api.put(`/cardapio/enderecos/${enderecoId}/cardapios`, payload).then((r) => r.data);

export const getCardapio = (id) =>
  api.get(`/cardapio/cardapios/${id}`).then((r) => r.data);

export const saveCardapio = async (data) => {
  const allowed = ['restaurante_id', 'foto_url', 'ativo', 'ordem', 'nome', 'disponibilidade', 'aceita_pedido', 'canais'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);

  const response = data.id
    ? await api.put(`/cardapio/cardapios/${data.id}`, body)
    : await api.post('/cardapio/cardapios', body);

  return response.data;
};

export const deleteCardapio = (id) =>
  api.delete(`/cardapio/cardapios/${id}`);

// --- Traduções de Cardápio ---
export const listCardapioTraducoes = (cardapioId) =>
  api.get(`/cardapio/cardapios/${cardapioId}/traducoes`).then((r) => r.data);

export const saveCardapioTraducao = async (cardapioId, data) => {
  const allowed = ['idioma', 'nome', 'descricao'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);
  const url = data.id
    ? `/cardapio/cardapios/traducoes/${data.id}`
    : `/cardapio/cardapios/${cardapioId}/traducoes`;
  const method = data.id ? 'put' : 'post';
  const response = await api[method](url, body);
  return response.data;
};

// --- Categorias ---
export const listCategorias = (cardapioId) =>
  api.get(`/cardapio/categorias?cardapioId=${cardapioId}`).then((r) => r.data);

export const getCategoria = (id) =>
  api.get(`/cardapio/categorias/${id}`).then((r) => r.data);

export const saveCategoria = async (data) => {
  const allowed = ['cardapio_id', 'ativo', 'ordem', 'nome'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);

  const response = data.id
    ? await api.put(`/cardapio/categorias/${data.id}`, body)
    : await api.post('/cardapio/categorias', body);

  return response.data;
};

export const deleteCategoria = (id) =>
  api.delete(`/cardapio/categorias/${id}`);

// --- Traduções de Categoria ---
export const listCategoriaTraducoes = (categoriaId) =>
  api.get(`/cardapio/categorias/${categoriaId}/traducoes`).then((r) => r.data);

export const saveCategoriaTraducao = async (categoriaId, data) => {
  const allowed = ['idioma', 'nome', 'descricao'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);
  const url = data.id
    ? `/cardapio/categorias/traducoes/${data.id}`
    : `/cardapio/categorias/${categoriaId}/traducoes`;
  const method = data.id ? 'put' : 'post';
  const response = await api[method](url, body);
  return response.data;
};

// --- Itens ---
export const listItens = (categoriaId, options = {}) =>
  api.get('/cardapio/itens', {
    params: {
      categoriaId,
      ...(options?.canal ? { canal: options.canal } : {}),
    },
  }).then((r) => r.data);

export const getItem = (id) =>
  api.get(`/cardapio/itens/${id}`).then((r) => r.data);

export const saveItem = async (data) => {
  const allowed = [
    'categoria_id', 'categoria_ids', 'foto_url', 'preco', 'preco_promocional', 'ativo',
    'preco_por_complemento', 'ordem', 'nome', 'descricao', 'codigo', 'complemento_ids', 'complementos', 'canais',
    'tipo', 'regra_preco_pizza', 'grupos_opcoes',
  ];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);

  const response = data.id
    ? await api.put(`/cardapio/itens/${data.id}`, body)
    : await api.post('/cardapio/itens', body);

  return response.data;
};

export const deleteItem = (id, categoriaId) =>
  api.delete(`/cardapio/itens/${id}`, { params: categoriaId ? { categoriaId } : undefined });

// --- Complementos ---
export const listComplementos = () => api.get('/cardapio/complementos').then((r) => r.data);
export const saveComplemento = async (data) => {
  const allowed = ['nome', 'preco', 'ativo'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);
  const res = data.id
    ? await api.put(`/cardapio/complementos/${data.id}`, body)
    : await api.post('/cardapio/complementos', body);
  return res.data;
};
export const deleteComplemento = (id) => api.delete(`/cardapio/complementos/${id}`);

// --- Mesas (QR) ---
export const listMesas = () => api.get('/cardapio/mesas').then((r) => r.data);
export const saveMesa = async (data) => {
  const allowed = ['codigo', 'descricao', 'ativo'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);
  const res = data.id
    ? await api.put(`/cardapio/mesas/${data.id}`, body)
    : await api.post('/cardapio/mesas', body);
  return res.data;
};
export const deleteMesa = (id) => api.delete(`/cardapio/mesas/${id}`);

// --- Pedidos (dashboard) ---
export const listPedidos = () => api.get('/cardapio/dashboard/pedidos').then((r) => r.data);
export const updateStatusPedido = (id, status) => api.put(`/cardapio/dashboard/pedidos/${id}/status`, { status }).then((r) => r.data);
export const addItemPedidoDashboard = (pedidoId, payload) =>
  api.post(`/cardapio/dashboard/pedidos/${pedidoId}/itens`, payload).then((r) => r.data);
export const updateItemPedidoDashboard = (pedidoId, pedidoItemId, payload) =>
  api.put(`/cardapio/dashboard/pedidos/${pedidoId}/itens/${pedidoItemId}`, payload).then((r) => r.data);
export const removeItemPedidoDashboard = (pedidoId, pedidoItemId) =>
  api.delete(`/cardapio/dashboard/pedidos/${pedidoId}/itens/${pedidoItemId}`).then((r) => r.data);

// --- Traduções de Item ---
export const listItemTraducoes = (itemId) =>
  api.get(`/cardapio/itens/${itemId}/traducoes`).then((r) => r.data);

export const saveItemTraducao = async (itemId, data) => {
  const allowed = ['idioma', 'nome', 'descricao'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);
  const url = data.id ? `/cardapio/itens/traducoes/${data.id}` : `/cardapio/itens/${itemId}/traducoes`;
  const method = data.id ? 'put' : 'post';
  const response = await api[method](url, body);
  return response.data;
};

// --- Tags ---
export const listTags = () => api.get('/cardapio/tags').then((r) => r.data);
export const getTag = (id) => api.get(`/cardapio/tags/${id}`).then((r) => r.data);
export const saveTag = async (data) => {
  const allowed = ['foto_url'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);
  const response = data.id ? await api.put(`/cardapio/tags/${data.id}`, body) : await api.post('/cardapio/tags', body);
  return response.data;
};
export const deleteTag = (id) => api.delete(`/cardapio/tags/${id}`);
export const saveTagTraducao = async (tagId, data) => {
  const allowed = ['idioma', 'nome'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);
  const url = data.id ? `/cardapio/tags/traducoes/${data.id}` : `/cardapio/tags/${tagId}/traducoes`;
  const method = data.id ? 'put' : 'post';
  const response = await api[method](url, body);
  return response.data;
};

// --- Relação Item <-> Tags ---
export const listItemTags = (itemId) => api.get(`/cardapio_item_tags?item_id=eq.${itemId}`).then((r) => r.data);
export const addItemTag = (itemId, tagId) => api.post('/cardapio_item_tags', { item_id: itemId, tag_id: tagId });
export const removeItemTag = (itemId, tagId) => api.delete(`/cardapio_item_tags?item_id=eq.${itemId}&tag_id=eq.${tagId}`);

// --- Grupos de Complemento ---
export const listGruposComplemento = (itemId) => api.get(`/cardapio/grupos-complemento?itemId=${itemId}`).then((r) => r.data);
export const saveGrupoComplemento = async (data) => {
  const allowed = ['item_id', 'qtd_minima', 'qtd_maxima', 'ativo'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);
  const response = data.id ? await api.put(`/cardapio/grupos-complemento/${data.id}`, body) : await api.post('/cardapio/grupos-complemento', body);
  return response.data;
};
export const deleteGrupoComplemento = (id) => api.delete(`/cardapio/grupos-complemento/${id}`);
export const listGrupoComplementoTraducoes = (grupoId) => api.get(`/cardapio/grupos-complemento/${grupoId}/traducoes`).then((r) => r.data);
export const saveGrupoComplementoTraducao = async (grupoId, data) => {
  const allowed = ['idioma', 'titulo'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);
  const url = data.id ? `/cardapio/grupos-complemento/traducoes/${data.id}` : `/cardapio/grupos-complemento/${grupoId}/traducoes`;
  const method = data.id ? 'put' : 'post';
  return api[method](url, body).then((r) => r.data);
};

// --- Opções de Complemento ---
export const listOpcoesComplemento = (grupoId) => api.get(`/cardapio/opcoes-complemento?grupoId=${grupoId}`).then((r) => r.data);
export const saveOpcaoComplemento = async (data) => {
  const allowed = ['grupo_complemento_id', 'preco', 'codigo_pdv', 'ativo'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);
  const response = data.id ? await api.put(`/cardapio/opcoes-complemento/${data.id}`, body) : await api.post('/cardapio/opcoes-complemento', body);
  return response.data;
};
export const deleteOpcaoComplemento = (id) => api.delete(`/cardapio/opcoes-complemento/${id}`);
export const listOpcaoComplementoTraducoes = (opcaoId) => api.get(`/cardapio/opcoes-complemento/${opcaoId}/traducoes`).then((r) => r.data);
export const saveOpcaoComplementoTraducao = async (opcaoId, data) => {
  const allowed = ['idioma', 'nome'];
  const body = data.id ? pick(data, ['id', ...allowed]) : pick(data, allowed);
  const url = data.id ? `/cardapio/opcoes-complemento/traducoes/${data.id}` : `/cardapio/opcoes-complemento/${opcaoId}/traducoes`;
  const method = data.id ? 'put' : 'post';
  return api[method](url, body).then((r) => r.data);
};

export const getCardapiosCompletos = async () => {
  const cards = await listCardapios();
  const withDetails = await Promise.all(
    cards.map(async (c) => {
      const categorias = await listCategorias(c.id);
      const categoriasWithItems = await Promise.all(
        categorias.map(async (cat) => ({ ...cat, itens: await listItens(cat.id) }))
      );
      return { ...c, categorias: categoriasWithItems };
    })
  );
  return withDetails;
};

// =================================================================
// MÓDULO CV
// =================================================================
const CV_BASE = '/cv';
const CV_PUBLIC_BASE = '/public/cv';

// --- Funções Públicas (Candidato) ---
export const getPublicOpenJobs = (slug) =>
  api.get(`${CV_PUBLIC_BASE}/open-jobs/${slug}`).then((r) => r.data);

export const getRestaurantSlug = (restaurantId) =>
  api.get(`${CV_PUBLIC_BASE}/restaurant/${restaurantId}/slug`).then((r) => r.data);

// ✅ CORRIGIDO: rota canônica e normalização do retorno
export const getJobForApplication = async (jobId) => {
  const { data } = await api.get(`${CV_PUBLIC_BASE}/jobs/${jobId}`);
  return {
    job: data?.job ?? data?.data?.job ?? null,
    questions: Array.isArray(data?.questions) ? data.questions : [],
  };
};

export const submitApplication = (payload) =>
  api.post(`${CV_PUBLIC_BASE}/applications`, payload).then((r) => r.data);

export const getNextStageForApplication = (applicationId) =>
  api.get(`${CV_PUBLIC_BASE}/applications/${applicationId}/next-stage`).then((r) => r.data);

export const submitStage = (applicationId, payload) =>
  api.post(`${CV_PUBLIC_BASE}/applications/${applicationId}/submit-stage`, payload).then((r) => r.data);

// --- Funções de Admin (RH) ---
export const getCvHealth = () =>
  api.get(`${CV_BASE}/health`).then((r) => r.data);

export const getRejectionReasons = () =>
  api.get(`${CV_BASE}/rejection-reasons`).then((r) => r.data);

export const createRejectionReason = (payload) =>
  api.post(`${CV_BASE}/rejection-reasons`, payload).then((r) => r.data);

export const updateRejectionReason = (id, payload) =>
  api.put(`${CV_BASE}/rejection-reasons/${id}`, payload).then((r) => r.data);

export const deleteRejectionReason = (id) =>
  api.delete(`${CV_BASE}/rejection-reasons/${id}`);

export const getCvJobsSummary = () =>
  api.get(`${CV_BASE}/jobs/summary`).then((r) => r.data);

export const getCvFormsSummary = () =>
  api.get(`${CV_BASE}/forms/summary`).then((r) => r.data);

export const getCvApplicationsSummary = () =>
  api.get(`${CV_BASE}/applications/summary`).then((r) => r.data);

export const getCvJobs = () =>
  api.get(`${CV_BASE}/jobs`).then((r) => r.data);

export const createCvJob = (payload) =>
  api.post(`${CV_BASE}/jobs`, payload).then((r) => r.data);

export const updateCvJob = (id, payload) =>
  api.put(`${CV_BASE}/jobs/${id}`, payload).then((r) => r.data);

export const deleteCvJob = (id) =>
  api.delete(`${CV_BASE}/jobs/${id}`);

export const getCvForms = (jobId) =>
  api.get(`${CV_BASE}/forms`, { params: jobId ? { jobId } : {} }).then((r) => r.data);

export const getCvFormDetails = (id) =>
  api.get(`${CV_BASE}/forms/${id}`).then(r => r.data);

export const createCvForm = (payload) =>
  api.post(`${CV_BASE}/forms`, payload).then(r => r.data);

export const updateCvForm = (id, payload) =>
  api.put(`${CV_BASE}/forms/${id}`, payload).then(r => r.data);

export const deleteCvForm = (id) =>
  api.delete(`${CV_BASE}/forms/${id}`);

export const togglePublishCvForm = (id, published) =>
  api.put(`${CV_BASE}/forms/${id}/publish`, { published }).then(r => r.data);

export const reorderCvForms = (ids) =>
  api.put(`${CV_BASE}/forms/reorder`, { ids }).then(r => r.data);

export const getCvFormQuestions = (formId) =>
  api.get(`${CV_BASE}/forms/${formId}/questions`).then(r => r.data);

export const createCvQuestion = (formId, payload) =>
  api.post(`${CV_BASE}/forms/${formId}/questions`, payload).then(r => r.data);

export const updateCvQuestion = (id, payload) =>
  api.put(`${CV_BASE}/questions/${id}`, payload).then(r => r.data);

export const deleteCvQuestion = (id) =>
  api.delete(`${CV_BASE}/questions/${id}`);

export const saveCvQuestionOptions = (questionId, options) =>
  api.put(`${CV_BASE}/questions/${questionId}/options`, { options });

export const saveCvQuestionRules = (questionId, rules) =>
  api.put(`${CV_BASE}/questions/${questionId}/rules`, { rules });

export const reorderCvQuestions = (formId, ids) =>
  api.put(`${CV_BASE}/forms/${formId}/questions/reorder`, { ids }).then(r => r.data);

export const getCvApplications = (filters = {}) =>
  api.get(`${CV_BASE}/applications`, {
      params: {
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.jobId ? { jobId: filters.jobId } : {}),
      },
    }).then((r) => r.data);

export const getCvApplication = (id) =>
  api.get(`${CV_BASE}/applications/${id}`).then((r) => r.data);

export const getCvApplicationDetails = (id) =>
  api.get(`${CV_BASE}/applications/${id}/details`).then((r) => r.data);

export const updateCvApplication = async (id, payload) => {
  try {
    const res = await api.put(`${CV_BASE}/applications/${id}`, payload);
    return res.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status !== 404 && status !== 405) throw error;

    try {
      const res = await api.put(`${CV_BASE}/applications/${id}/status`, payload);
      return res.data;
    } catch (fallbackError) {
      const fallbackStatus = fallbackError?.response?.status;
      if (fallbackStatus !== 404 && fallbackStatus !== 405) throw fallbackError;

      const patchRes = await api.patch(`${CV_BASE}/applications/${id}`, payload);
      return patchRes.data;
    }
  }
};

export const deleteCvApplication = (id) =>
  api.delete(`${CV_BASE}/applications/${id}`);

// -------------------- PEDIDOS (cliente) --------------------
export const getPedidoPorMesa = async (mesa) => {
  const res = await api.get(`/cardapio/pedidos/mesa/${encodeURIComponent(mesa)}`);
  return res.data;
};

export const criarPedido = async ({ restauranteId, mesa }) => {
  const res = await api.post('/cardapio/pedidos', { restauranteId, mesa });
  return res.data;
};

export const adicionarItemPedido = async (pedidoId, itemPayload) => {
  const res = await api.post(`/cardapio/pedidos/${pedidoId}/itens`, itemPayload);
  return res.data;
};

export const enviarPedido = async (pedidoId) => {
  const res = await api.put(`/cardapio/pedidos/${pedidoId}/enviar`, {});
  return res.data;
};

// CV - Reordenar ETAPAS (Stages) de uma vaga
export const reorderCvStages = (jobId, ids) =>
  api.put(`${CV_BASE}/jobs/${jobId}/stages/reorder`, { ids }).then(r => r.data);

// =================================================================
// MÓDULO DE Campanha e segmentação
// =================================================================
// Segmentações
export const criarSegmentacao = (payload) =>
  api.post('/smart/segmentacao', payload).then(r => r.data);

export const listarSegmentacoes = () =>
  api.get('/smart/segmentacao').then(r => r.data);

export const atualizarSegmentacao = (id, payload) =>
  api.put(`/smart/segmentacao/${id}`, payload).then(r => r.data);

export const deletarSegmentacao = (id) =>
  api.delete(`/smart/segmentacao/${id}`).then(r => r.data);

// Campanhas
export const listarCampanhas = () =>
  api.get('/smart/campanhas').then(r => r.data);

export const obterCampanha = (id) =>
  api.get(`/smart/campanhas/${id}`).then(r => r.data);

export const criarCampanha = (formData, isMultipart = false) =>
  api.post('/smart/campanhas', formData, isMultipart ? {
    headers: { 'Content-Type': 'multipart/form-data' }
  } : undefined);

export const atualizarCampanha = (id, formData, isMultipart = false) =>
  api.put(`/smart/campanhas/${id}`, formData, isMultipart ? {
    headers: { 'Content-Type': 'multipart/form-data' }
  } : undefined);

export const deletarCampanha = (id) =>
  api.delete(`/smart/campanhas/${id}`).then(r => r.data);

export default api;
